import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Image,
  Font,
  Link,
} from "@react-pdf/renderer";
import moment from "moment";

const styles = StyleSheet.create({
  page: {
    fontFamily: "Helvetica",
    fontSize: 9,
    padding: 40,
    lineHeight: 1.4,
    color: "#000",
  },
  title: {
    fontSize: 18,
    textAlign: "center",
    color: "#0b57d0",
    fontWeight: "bold",
    marginBottom: 10,
  },
  section: {
    marginBottom: 10,
  },
  bold: {
    fontWeight: "bold",
  },
  headerLine: {
    borderBottomWidth: 1,
    borderBottomColor: "#ccc",
    borderBottomStyle: "solid",
    marginBottom: 6,
    paddingBottom: 4,
  },
  row: {
    flexDirection: "row",
    justifyContent: "space-between",
    gap: 10,
  },
  column: {
    flex: 1,
    paddingRight: 5,
  },
  columnBox: {
    width: "33.33%",
    padding: 4,
  },
});

const twoDecimal = (x) => {
  const num = Number.parseFloat(x).toFixed(2);
  const [integerPart, decimalPart] = num.split(".");
  const formattedInteger = integerPart.replace(/(\d)(?=(\d\d)+\d$)/g, "$1,");
  return `${formattedInteger}.${decimalPart}`;
};

function ProformaGeneratePdf({ proformaDetails, projectDetails, paymentList, companyLogo, items = [] }) {
  const finalTotal = parseFloat(proformaDetails.total_with_gst || 0);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Title Header */}
        <View style={{ backgroundColor: "#0b57d0", padding: 8, borderRadius: 4 }}>
          <Text style={{ color: "white", fontSize: 16, fontWeight: "bold", textAlign: "center" }}>
            Proforma Invoice
          </Text>
        </View>

        {/* Proforma Number & Date */}
        <View style={{ flexDirection: "row", justifyContent: "space-between", borderBottomWidth: 1, borderBottomColor: "#000", marginTop: 8, paddingBottom: 4 }}>
          <Text style={styles.bold}>Proforma No: {proformaDetails.proforma_no}</Text>
          <Text style={styles.bold}>Date: {moment(proformaDetails.proforma_date).format("DD-MM-YYYY")}</Text>
        </View>

        {/* Company Info Header */}
        <View style={[styles.row, { marginTop: 10, borderBottomWidth: 1, borderBottomColor: "#000", paddingBottom: 10 }]}>
          <View style={{ width: "30%", justifyContent: "center", alignItems: "center" }}>
            {companyLogo && <Image src={companyLogo} style={{ width: 60, height: 60 }} />}
          </View>
          <View style={{ width: "70%", paddingLeft: 10 }}>
            <Text style={{ fontSize: 12, fontWeight: "bold", color: "#0b57d0" }}>
              {proformaDetails.company_Name || "KG Build Tech"}
            </Text>
            <Text style={{ fontSize: 9, marginTop: 4 }}>
              {proformaDetails.company_address || "Plot No 108, Perumal Koil Street, Alapakkam Porur, Chennai - 600116"}
            </Text>
            <Text style={{ fontSize: 9, marginTop: 4 }}>
              GST NO: {proformaDetails.company_gst || "33XXXXXXXXXXXXX"}
            </Text>
          </View>
        </View>

        {/* Client & Billing Info */}
        <View style={[styles.row, { marginTop: 12 }]}>
          <View style={styles.column}>
            <Text style={styles.bold}>Client Name / Bill To:</Text>
            <Text style={{ fontSize: 10, color: "#0056b3", fontWeight: "bold", marginTop: 4 }}>
              {proformaDetails.vendor_name}
            </Text>
            <Text style={{ marginTop: 2 }}>{proformaDetails.vendor_street}, {proformaDetails.vendor_city}</Text>
            <Text style={{ marginTop: 2 }}>GST: {proformaDetails.vendor_gst}</Text>
          </View>

          <View style={styles.column}>
            <Text style={styles.bold}>Ship To / Project Site:</Text>
            <Text style={{ fontSize: 10, color: "#0056b3", fontWeight: "bold", marginTop: 4 }}>
              {proformaDetails.project_name}
            </Text>
            <Text style={{ marginTop: 2 }}>Site Contact: {proformaDetails.site_eng_contact}</Text>
          </View>
        </View>

        {/* Items Table */}
        <View style={{ marginTop: 15, borderWidth: 1, borderColor: "#000" }}>
          {/* Header Row */}
          <View style={{ flexDirection: "row", backgroundColor: "#f1f5f9", borderBottomWidth: 1, borderBottomColor: "#000" }}>
            <Text style={{ width: "5%", padding: 4, fontWeight: "bold", textAlign: "center" }}>S.No</Text>
            <Text style={{ width: "40%", padding: 4, fontWeight: "bold" }}>Item Name & Description</Text>
            <Text style={{ width: "10%", padding: 4, fontWeight: "bold", textAlign: "center" }}>Qty</Text>
            <Text style={{ width: "10%", padding: 4, fontWeight: "bold", textAlign: "center" }}>Unit</Text>
            <Text style={{ width: "15%", padding: 4, fontWeight: "bold", textAlign: "right" }}>Rate (₹)</Text>
            <Text style={{ width: "20%", padding: 4, fontWeight: "bold", textAlign: "right" }}>Total (₹)</Text>
          </View>

          {/* Data Rows */}
          {items.map((item, index) => (
            <View key={index} style={{ flexDirection: "row", borderBottomWidth: index < items.length - 1 ? 1 : 0, borderBottomColor: "#ccc" }}>
              <Text style={{ width: "5%", padding: 4, textAlign: "center" }}>{index + 1}</Text>
              <View style={{ width: "40%", padding: 4 }}>
                <Text style={styles.bold}>{item.item_name}</Text>
                <Text style={{ fontSize: 8, color: "#555" }}>{item.item_description}</Text>
              </View>
              <Text style={{ width: "10%", padding: 4, textAlign: "center" }}>{item.quantity}</Text>
              <Text style={{ width: "10%", padding: 4, textAlign: "center" }}>{item.unit}</Text>
              <Text style={{ width: "15%", padding: 4, textAlign: "right" }}>{twoDecimal(item.rate)}</Text>
              <Text style={{ width: "20%", padding: 4, textAlign: "right" }}>{twoDecimal(item.total)}</Text>
            </View>
          ))}
        </View>

        {/* Totals Box */}
        <View style={{ marginTop: 15, alignSelf: "flex-end", width: "40%", borderTopWidth: 1, borderTopColor: "#000", paddingTop: 5 }}>
          <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 3 }}>
            <Text>Subtotal:</Text>
            <Text>₹{twoDecimal(proformaDetails.total_without_gst)}</Text>
          </View>
          <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 3 }}>
            <Text>Tax (CGST/SGST/IGST):</Text>
            <Text>₹{twoDecimal(parseFloat(proformaDetails.total_cgst || 0) + parseFloat(proformaDetails.total_sgst || 0) + parseFloat(proformaDetails.total_igst || 0))}</Text>
          </View>
          <View style={{ flexDirection: "row", justifyContent: "space-between", marginBottom: 3 }}>
            <Text>Packing Charges:</Text>
            <Text>₹{twoDecimal(proformaDetails.packing_charge)}</Text>
          </View>
          <View style={{ flexDirection: "row", justifyContent: "space-between", borderTopWidth: 1, borderTopColor: "#000", paddingTop: 3 }}>
            <Text style={styles.bold}>Grand Total:</Text>
            <Text style={styles.bold}>₹{twoDecimal(finalTotal)}</Text>
          </View>
        </View>

        {/* Terms and Conditions */}
        <View style={{ marginTop: 25, borderTopWidth: 1, borderTopColor: "#ccc", paddingTop: 10 }}>
          <Text style={[styles.bold, { fontSize: 10, marginBottom: 5 }]}>Terms & Conditions</Text>
          <Text style={{ fontSize: 8, color: "#444" }}>{proformaDetails.terms || "Standard payment terms apply."}</Text>
        </View>
      </Page>
    </Document>
  );
}

export default ProformaGeneratePdf;
